import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  cloneElement,
} from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { OutlineButton } from "@/components/common/outline-button";
import { TextButton } from "@/components/common/text-button";
import { Image } from "@/components/common/image";
import { Video } from "@/components/common/video";
import { cropImage } from "@/utils/image-crop";
import { cropVideo } from "@/utils/video-crop";
import { cn } from "@/utils/cn";
import { CropIcon } from "@/components/custom-digital-human/assets";

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

// 新增：裁剪坐标接口
interface CropCoords {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropModalProps {
  // trigger 模式
  trigger?: React.ReactElement;
  // open 参数模式
  open?: boolean;
  // 通用属性
  file: File | null;
  aspectRatio?: number; // 裁剪比例，如 9/16
  // 新增：默认裁剪坐标数据
  defaultCropCoords?: CropCoords;
  onOpenChange?: (open: boolean) => void;
  // 修改：onCropComplete 现在返回 blob 和 cropCoords
  onCropComplete: (croppedImageBlob: Blob, cropCoords: CropCoords) => void;
}

export const CropModal: React.FC<CropModalProps> = ({
  trigger,
  open: externalOpen,
  file,
  aspectRatio = 9 / 16, // 默认 9:16
  defaultCropCoords, // 新增：默认裁剪坐标
  onOpenChange,
  onCropComplete,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaDimensions, setMediaDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [cropArea, setCropArea] = useState<CropArea>({
    x: 50,
    y: 50,
    width: 200,
    height: 200 / aspectRatio,
  });
  // 新增：存储初始裁剪区域（用于reset）
  const [initialCropArea, setInitialCropArea] = useState<CropArea>({
    x: 50,
    y: 50,
    width: 200,
    height: 200 / aspectRatio,
  });
  // 新增：存储默认裁剪区域（如果有默认数据）
  const [defaultCropArea, setDefaultCropArea] = useState<CropArea | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [cropStart, setCropStart] = useState<CropArea>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLImageElement & HTMLVideoElement>(null);

  // 判断是否为 trigger 模式
  const isTriggerMode = trigger !== undefined;
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理弹窗开关
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isTriggerMode) {
        setInternalOpen(open);
      }
      onOpenChange?.(open);
    },
    [isTriggerMode, onOpenChange]
  );

  const loadImageDimensions = (
    url: string
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };
      img.src = url;
    });
  };

  // 加载视频尺寸
  const loadVideoDimensions = (
    url: string
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.muted = true;
      video.preload = "metadata";

      let resolved = false;

      const handleSuccess = () => {
        if (resolved) return;
        resolved = true;

        if (video.videoWidth > 0 && video.videoHeight > 0) {
          resolve({ width: video.videoWidth, height: video.videoHeight });
        } else {
          reject(new Error("Video dimensions are not available"));
        }
      };

      const handleError = (error: string) => {
        if (resolved) return;
        resolved = true;
        reject(new Error(error));
      };

      // 当视频元数据加载完成时，尺寸信息就已经可用
      video.onloadedmetadata = () => {
        handleSuccess();
      };

      // 备用事件：当视频数据加载完成时
      video.onloadeddata = () => {
        if (!resolved) {
          handleSuccess();
        }
      };

      video.onerror = () => {
        handleError("Failed to load video");
      };

      video.src = url;
    });
  };

  // 新增：将cropCoords转换为cropArea的函数
  const convertCropCoordsToArea = useCallback((cropCoords: CropCoords, mediaWidth: number, mediaHeight: number): CropArea => {
    const containerWidth = containerRef.current?.clientWidth || 600;
    const containerHeight = containerRef.current?.clientHeight || 400;

    // 计算媒体在容器中的显示尺寸
    const mediaAspectRatio = mediaWidth / mediaHeight;
    const containerAspectRatio = containerWidth / containerHeight;

    let displayWidth: number;
    let displayHeight: number;
    if (mediaAspectRatio > containerAspectRatio) {
      displayWidth = containerWidth;
      displayHeight = containerWidth / mediaAspectRatio;
    } else {
      displayHeight = containerHeight;
      displayWidth = containerHeight * mediaAspectRatio;
    }

    // 计算图像在容器中的偏移位置（居中显示）
    const imageOffsetX = (containerWidth - displayWidth) / 2;
    const imageOffsetY = (containerHeight - displayHeight) / 2;

    // 计算缩放比例
    const scaleX = displayWidth / mediaWidth;
    const scaleY = displayHeight / mediaHeight;

    // 将原始坐标转换为显示坐标
    const cropArea: CropArea = {
      x: imageOffsetX + cropCoords.x * scaleX,
      y: imageOffsetY + cropCoords.y * scaleY,
      width: cropCoords.width * scaleX,
      height: cropCoords.height * scaleY,
    };

    return cropArea;
  }, []);

  const initializeCropArea = (mediaWidth: number, mediaHeight: number) => {
    if (!containerRef.current) {
      return;
    }
    // 初始化裁剪框位置和大小
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    // 计算媒体在容器中的显示尺寸
    const mediaAspectRatio = mediaWidth / mediaHeight;
    const containerAspectRatio = containerWidth / containerHeight;

    let displayWidth: number;
    let displayHeight: number;
    if (mediaAspectRatio > containerAspectRatio) {
      displayWidth = containerWidth;
      displayHeight = containerWidth / mediaAspectRatio;
    } else {
      displayHeight = containerHeight;
      displayWidth = containerHeight * mediaAspectRatio;
    }

    // 计算图像在容器中的偏移位置（居中显示）
    const imageOffsetX = (containerWidth - displayWidth) / 2;
    const imageOffsetY = (containerHeight - displayHeight) / 2;

    let initialCropArea: CropArea;
    let defaultArea: CropArea | null = null;

    // 如果有默认裁剪坐标，使用默认坐标
    if (defaultCropCoords) {
      defaultArea = convertCropCoordsToArea(defaultCropCoords, mediaWidth, mediaHeight);
      initialCropArea = { ...defaultArea };
    } else {
      // 否则使用原来的逻辑
      const cropRatio = 1;
      const maxCropWidth = displayWidth * cropRatio;
      const maxCropHeight = displayHeight * cropRatio;

      // 根据宽高比确定实际裁剪框尺寸
      let initialWidth: number;
      let initialHeight: number;

      if (maxCropWidth / aspectRatio <= maxCropHeight) {
        // 宽度限制
        initialWidth = maxCropWidth;
        initialHeight = maxCropWidth / aspectRatio;
      } else {
        // 高度限制
        initialHeight = maxCropHeight;
        initialWidth = maxCropHeight * aspectRatio;
      }

      // 计算裁剪框的居中位置（相对于图像）
      const cropX = imageOffsetX + (displayWidth - initialWidth) / 2;
      const cropY = imageOffsetY + (displayHeight - initialHeight) / 2;

      initialCropArea = {
        x: cropX,
        y: cropY,
        width: initialWidth,
        height: initialHeight,
      };
    }

    // 存储初始状态和默认状态
    setInitialCropArea(initialCropArea);
    setDefaultCropArea(defaultArea);
    setCropArea(initialCropArea);
  };

  // 加载图片
  useEffect(() => {
    if (file && currentOpen) {
      const url = URL.createObjectURL(file);
      setMediaUrl(url);

      const loadDimensions = async () => {
        try {
          let dimensions: { width: number; height: number };

          if (file.type.startsWith("image/")) {
            dimensions = await loadImageDimensions(url);
          } else if (file.type.startsWith("video/")) {
            dimensions = await loadVideoDimensions(url);
          } else {
            throw new Error("Unsupported file type");
          }

          setMediaDimensions(dimensions);
          initializeCropArea(dimensions.width, dimensions.height);
        } catch (error) {
          console.error("Failed to load media dimensions:", error);
        }
      };

      loadDimensions();

      return () => URL.revokeObjectURL(url);
    }
  }, [file, currentOpen, aspectRatio, defaultCropCoords]);

  // 获取鼠标相对于容器的位置
  const getRelativePosition = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  // 获取图片显示区域的边界
  const getImageBounds = useCallback(() => {
    if (!mediaRef.current || !containerRef.current) {
      return { left: 0, top: 0, right: 600, bottom: 400 };
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const mediaRect = mediaRef.current.getBoundingClientRect();

    return {
      left: mediaRect.left - containerRect.left,
      top: mediaRect.top - containerRect.top,
      right: mediaRect.right - containerRect.left,
      bottom: mediaRect.bottom - containerRect.top,
    };
  }, []);

  // 限制裁剪框在图片边界内
  const constrainCropArea = useCallback(
    (area: CropArea): CropArea => {
      const bounds = getImageBounds();

      // 最小尺寸限制（50px）
      const minSize = 50;
      const minWidth = minSize;
      const minHeight = minSize / aspectRatio;

      // 最大尺寸限制（图片边界）
      const maxWidth = bounds.right - bounds.left;
      const maxHeight = bounds.bottom - bounds.top;

      let { x, y, width, height } = area;

      // 限制尺寸
      width = Math.max(minWidth, Math.min(width, maxWidth));
      height = Math.max(minHeight, Math.min(height, maxHeight));

      // 保持宽高比
      if (width / height !== aspectRatio) {
        if (width / aspectRatio > height) {
          width = height * aspectRatio;
        } else {
          height = width / aspectRatio;
        }
      }

      // 限制位置
      x = Math.max(bounds.left, Math.min(x, bounds.right - width));
      y = Math.max(bounds.top, Math.min(y, bounds.bottom - height));

      return { x, y, width, height };
    },
    [aspectRatio, getImageBounds]
  );

  // 开始拖拽裁剪框
  const handleCropMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const pos = getRelativePosition(e);
      setIsDragging(true);
      setDragStart(pos);
      setCropStart(cropArea);
    },
    [cropArea, getRelativePosition]
  );

  // 开始调整裁剪框大小
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent, handle: string) => {
      e.preventDefault();
      e.stopPropagation();
      const pos = getRelativePosition(e);
      setIsResizing(true);
      setResizeHandle(handle);
      setDragStart(pos);
      setCropStart(cropArea);
    },
    [cropArea, getRelativePosition]
  );

  // 鼠标移动处理
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging && !isResizing) return;

      const pos = getRelativePosition(e);
      const deltaX = pos.x - dragStart.x;
      const deltaY = pos.y - dragStart.y;

      if (isDragging) {
        // 拖拽移动
        const newArea = {
          ...cropStart,
          x: cropStart.x + deltaX,
          y: cropStart.y + deltaY,
        };
        setCropArea(constrainCropArea(newArea));
      } else if (isResizing && resizeHandle) {
        // 调整大小
        const newArea = { ...cropStart };

        switch (resizeHandle) {
          case "nw": // 左上角
            newArea.width = cropStart.width - deltaX;
            newArea.height = newArea.width / aspectRatio;
            newArea.x = cropStart.x + cropStart.width - newArea.width;
            newArea.y = cropStart.y + cropStart.height - newArea.height;
            break;
          case "ne": // 右上角
            newArea.width = cropStart.width + deltaX;
            newArea.height = newArea.width / aspectRatio;
            newArea.y = cropStart.y + cropStart.height - newArea.height;
            break;
          case "sw": // 左下角
            newArea.width = cropStart.width - deltaX;
            newArea.height = newArea.width / aspectRatio;
            newArea.x = cropStart.x + cropStart.width - newArea.width;
            break;
          case "se": // 右下角
            newArea.width = cropStart.width + deltaX;
            newArea.height = newArea.width / aspectRatio;
            break;
        }

        setCropArea(constrainCropArea(newArea));
      }
    },
    [
      isDragging,
      isResizing,
      resizeHandle,
      dragStart,
      cropStart,
      getRelativePosition,
      constrainCropArea,
      aspectRatio,
    ]
  );

  // 鼠标释放处理
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  // 保存裁剪
  const handleSave = useCallback(async () => {
    if (!file || !mediaRef.current) return;

    try {
      const bounds = getImageBounds();
      const containerRect = containerRef.current?.getBoundingClientRect();

      if (!containerRect) return;

      // 计算裁剪区域相对于原图的坐标
      const scaleX = mediaDimensions.width / (bounds.right - bounds.left);
      const scaleY = mediaDimensions.height / (bounds.bottom - bounds.top);

      const cropCoords: CropCoords = {
        x: (cropArea.x - bounds.left) * scaleX,
        y: (cropArea.y - bounds.top) * scaleY,
        width: cropArea.width * scaleX,
        height: cropArea.height * scaleY,
      };

      // 使用裁剪工具函数
      const crop = file.type.startsWith("image") ? cropImage : cropVideo;
      const croppedBlob = await crop(file, [
        { x: cropCoords.x, y: cropCoords.y },
        { x: cropCoords.x + cropCoords.width, y: cropCoords.y },
        {
          x: cropCoords.x + cropCoords.width,
          y: cropCoords.y + cropCoords.height,
        },
        { x: cropCoords.x, y: cropCoords.y + cropCoords.height },
      ]);

      // 修改：同时传递 blob 和 cropCoords
      onCropComplete(new File([croppedBlob], file.name, { type: file.type }), cropCoords);
      handleOpenChange(false);
    } catch (error) {
      console.error("crop error:", error);
    }
  }, [
    file,
    cropArea,
    mediaDimensions,
    getImageBounds,
    onCropComplete,
    handleOpenChange,
  ]);

  // 修改：重置按钮逻辑
  const handleReset = useCallback(() => {
    // 如果有默认裁剪区域，重置到默认状态
    if (defaultCropArea) {
      setCropArea({ ...defaultCropArea });
    } else {
      // 否则重置到初始状态
      setCropArea({ ...initialCropArea });
    }
    setIsDragging(false);
    setIsResizing(false);
  }, [defaultCropArea, initialCropArea]);

  // 关闭弹窗
  const handleClose = useCallback(() => {
    setMediaUrl(null);
    setCropArea({ x: 50, y: 50, width: 200, height: 200 / aspectRatio });
    setIsDragging(false);
    setIsResizing(false);
    handleOpenChange(false);
  }, [aspectRatio, handleOpenChange]);

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 加载图片
  useEffect(() => {
    if (file && currentOpen) {
      const url = URL.createObjectURL(file);
      setMediaUrl(url);

      const loadDimensions = async () => {
        try {
          let dimensions: { width: number; height: number };

          if (file.type.startsWith("image/")) {
            dimensions = await loadImageDimensions(url);
          } else if (file.type.startsWith("video/")) {
            dimensions = await loadVideoDimensions(url);
          } else {
            throw new Error("Unsupported file type");
          }

          setMediaDimensions(dimensions);
          initializeCropArea(dimensions.width, dimensions.height);
        } catch (error) {
          console.error("Failed to load media dimensions:", error);
        }
      };

      loadDimensions();

      return () => URL.revokeObjectURL(url);
    }
  }, [file, currentOpen, aspectRatio, defaultCropCoords]);

  // 弹窗内容
  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col z-50 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-xl",
          "w-[42rem] h-[35rem] max-w-[90vw] max-h-[90vh]"
        )}
      >
        {/* 头部 */}
        <div className="flex justify-between items-center mb-[1.4rem] pl-[35px] pr-6 pt-6">
          <Dialog.Title className="flex">
            <div className="size-12 flex justify-center items-center rounded-md border border-[#d5d7da] mr-4">
              <CropIcon className="size-5" />
            </div>
            <div>
              <div className="text-i-normal text-lg font-semibold">
                {t("custom_digital_human.upload_form.crop_photo")}
              </div>
              <span className="text-sm text-[rgba(83,88,98,1)]">
                {t("custom_digital_human.upload_form.crop_photo_tips")}
              </span>
            </div>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4" />
            </button>
          </Dialog.Close>
        </div>

        {/* 裁剪区域 */}
        <div
          ref={containerRef}
          className="relative mx-[35px] mb-8 bg-[rgba(249,250,251,1)] flex-1 cursor-crosshair rounded-[1.25rem]"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {mediaUrl && (
            <>
              {/* 图片 */}
              {file.type.startsWith("image/") ? (
                <Image
                  ref={mediaRef}
                  src={mediaUrl}
                  alt="Crop preview"
                  className={cn(
                    "max-w-full max-h-full object-contain",
                    "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  )}
                  draggable={false}
                />
              ) : (
                <Video
                  ref={mediaRef}
                  src={mediaUrl}
                  className={cn(
                    "max-w-full max-h-full object-contain",
                    "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  )}
                  preload="metadata"
                  draggable={false}
                />
              )}

              {/* 裁剪框外的蒙层 */}
              {/* 上 */}
              <div className="absolute w-full opacity-50 bg-white backdrop-blur-sm" style={{
                top: 0,
                height: cropArea.y,
              }} />
              {/* 下 */}
              <div className="absolute w-full opacity-50 bg-white" style={{
                top: cropArea.y + cropArea.height,
                bottom: 0,
              }} />
              {/* 左 */}
              <div className="absolute opacity-50 bg-white" style={{
                top: cropArea.y,
                left: 0,
                width: cropArea.x,
                height: cropArea.height,
              }} />
              {/* 右 */}
              <div className="absolute opacity-50 bg-white" style={{
                top: cropArea.y,
                left: cropArea.x + cropArea.width,
                right: 0,
                height: cropArea.height,
              }} />

              {/* 裁剪框 */}
              <div
                className={cn(
                  "absolute border-2 border-dashed border-[rgba(117,66,247,1)] cursor-move"
                )}
                style={{
                  left: cropArea.x,
                  top: cropArea.y,
                  width: cropArea.width,
                  height: cropArea.height,
                }}
                onMouseDown={handleCropMouseDown}
              >
                {/* 调整手柄 */}
                {["nw", "ne", "sw", "se"].map((handle) => (
                  <div
                    key={handle}
                    className={cn(
                      "absolute w-3 h-3 bg-white border-2 border-[rgba(117,66,247,1)] cursor-pointer",
                      "hover:bg-[rgba(117,66,247,1)] transition-colors",
                      {
                        "top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nw-resize":
                          handle === "nw",
                        "top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-ne-resize":
                          handle === "ne",
                        "bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-sw-resize":
                          handle === "sw",
                        "bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-se-resize":
                          handle === "se",
                      }
                    )}
                    onMouseDown={(e) => handleResizeMouseDown(e, handle)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* 底部按钮 */}
        <div className={cn("mb-8 flex justify-center items-center gap-3")}>
          {/* 修改：Reset按钮现在调用handleReset */}
          <OutlineButton className="w-56" label={t("custom_digital_human.upload_form.reset")} onClick={handleReset} />
          <TextButton className="w-56" label={t("custom_digital_human.upload_form.save")} onClick={handleSave} />
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
};
